import type { ShippingStatus } from '../../lib/api/operatorConsole';
import { STATUS_OPTIONS, type ShippingFilterValues } from './shippingFilters';

interface Props {
  values: ShippingFilterValues;
  onChange: (values: ShippingFilterValues) => void;
}

const fieldClass =
  'rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-ui-text ' +
  'placeholder:text-ui-text-secondary focus:outline-none focus:ring-2 focus:ring-[var(--co-brand)]';

export function ShippingsFilters({ values, onChange }: Props) {
  function set<K extends keyof ShippingFilterValues>(key: K, value: ShippingFilterValues[K]) {
    onChange({ ...values, [key]: value });
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ui-border bg-ui-card p-3">
      <select
        aria-label="Status filter"
        value={values.status}
        onChange={(e) => set('status', e.target.value as ShippingStatus | '')}
        className={fieldClass}
      >
        {STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Both are exact "find this one" lookups, not searches: support arrives
          with a number from a ticket. */}
      <input
        type="text"
        inputMode="numeric"
        aria-label="Shipping id filter"
        placeholder="Shipping ID"
        value={values.id}
        onChange={(e) => set('id', e.target.value)}
        className={`${fieldClass} w-32`}
      />

      <input
        type="text"
        aria-label="Certificate number filter"
        placeholder="Certificate #"
        value={values.certificate_number}
        onChange={(e) => set('certificate_number', e.target.value)}
        className={`${fieldClass} w-40`}
      />

      <input
        type="text"
        inputMode="numeric"
        aria-label="User id filter"
        placeholder="User id"
        value={values.user_id}
        onChange={(e) => set('user_id', e.target.value)}
        className={`${fieldClass} w-28`}
      />

      <input
        type="email"
        aria-label="User email filter"
        placeholder="User email"
        value={values.user_email}
        onChange={(e) => set('user_email', e.target.value)}
        className={`${fieldClass} w-56`}
      />

      <input
        type="text"
        aria-label="Tracking number filter"
        placeholder="Tracking number"
        value={values.tracking_number}
        onChange={(e) => set('tracking_number', e.target.value)}
        className={`${fieldClass} w-44`}
      />

      {/* Both ends are inclusive calendar days — the backend stretches
          `created_to` to the end of that day. */}
      <label className="flex items-center gap-2 text-sm text-ui-text-secondary">
        From
        <input
          type="date"
          aria-label="Created from"
          value={values.created_from}
          onChange={(e) => set('created_from', e.target.value)}
          className={fieldClass}
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-ui-text-secondary">
        To
        <input
          type="date"
          aria-label="Created to"
          value={values.created_to}
          onChange={(e) => set('created_to', e.target.value)}
          className={fieldClass}
        />
      </label>
    </div>
  );
}
